import { useEffect, useRef } from 'react';

export interface UseScrollRevealOptions {
  threshold?: number | number[];
  rootMargin?: string;
  triggerOnce?: boolean;
}

/**
 * Custom hook that uses Intersection Observer to add a "revealed" class
 * to elements when they enter the viewport. Perfect for scroll-triggered
 * animations and progressive content reveals.
 *
 * @param options - Configuration options for the observer
 * @returns A React ref to attach to the element you want to reveal
 *
 * @example
 * const ref = useScrollReveal({ threshold: 0.1 });
 * return <div ref={ref}>Content that reveals on scroll</div>;
 */
export function useScrollReveal(options: UseScrollRevealOptions = {}) {
  const ref = useRef<HTMLDivElement | null>(null);
  const hasRevealed = useRef(false);

  const {
    threshold = 0.1,
    rootMargin = '0px 0px -50px 0px',
    triggerOnce = true,
  } = options;

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    // If already revealed and triggerOnce is true, skip observer setup
    if (triggerOnce && hasRevealed.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('revealed');
            if (triggerOnce) {
              hasRevealed.current = true;
              observer.unobserve(entry.target);
            }
          } else if (!triggerOnce) {
            // Only remove class if triggerOnce is false
            entry.target.classList.remove('revealed');
          }
        });
      },
      {
        threshold,
        rootMargin,
      }
    );

    observer.observe(element);

    return () => {
      if (element) {
        observer.unobserve(element);
      }
      observer.disconnect();
    };
  }, [threshold, rootMargin, triggerOnce]);

  return ref;
}
